/**
 * AdminTeachingExcellence — admin-editable thresholds for the
 * Teaching Excellence tier (Bronze → Black) of the Trust Stack.
 *
 * Mirrors AdminPricingSettings: load → edit → preview → save → audit history.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Save, RotateCcw, Loader2, Sparkles,
  History, SlidersHorizontal, Award, Target,
} from "lucide-react";
import useSafeBack from "../hooks/useSafeBack";

const API = process.env.REACT_APP_BACKEND_URL;

const TIERS = [
  { key: "bronze",   label: "Bronze",   ring: "ring-amber-300",  bg: "bg-amber-100   text-amber-800" },
  { key: "silver",   label: "Silver",   ring: "ring-slate-300",  bg: "bg-slate-200   text-slate-700" },
  { key: "gold",     label: "Gold",     ring: "ring-yellow-300", bg: "bg-yellow-100  text-yellow-800" },
  { key: "platinum", label: "Platinum", ring: "ring-cyan-300",   bg: "bg-cyan-100    text-cyan-700" },
  { key: "black",    label: "Black",    ring: "ring-slate-700",  bg: "bg-slate-900   text-white" },
];

function tierForScore(score, s) {
  if (typeof score !== "number") return null;
  let earned = null;
  for (const t of TIERS) {
    if (score >= s[`${t.key}_score`]) earned = t.key;
    else break;
  }
  return earned;
}

const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function AdminTeachingExcellence() {
  useSafeBack();
  const [tab, setTab] = useState("settings");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [note, setNote] = useState("");
  const [previewScore, setPreviewScore] = useState(82);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/teaching-excellence/settings`, { withCredentials: true });
      setSettings(data.settings);
      setDefaults(data.defaults);
    } catch (e) {
      toast.error("Could not load settings", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }

  async function loadHistory() {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/teaching-excellence/settings/history?limit=50`, { withCredentials: true });
      setHistory(data.items || []);
    } catch (e) {
      toast.error("Could not load history", { description: e?.response?.data?.detail || e.message });
    } finally {
      setHistoryLoading(false);
    }
  }

  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab]);

  function setField(k, v) {
    setSettings((s) => ({ ...s, [k]: Number(v) }));
  }

  async function save() {
    if (!settings) return;
    setSaving(true);
    try {
      const payload = { ...settings, note: note || undefined };
      const { data } = await axios.put(`${API}/api/teaching-excellence/settings`, payload, { withCredentials: true });
      setSettings(data.settings);
      setNote("");
      toast.success(data.changes_logged ? `Saved (${data.changes_logged} change${data.changes_logged === 1 ? "" : "s"} logged)` : "No changes to save");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  async function reset() {
    if (!window.confirm("Reset Teaching Excellence thresholds to defaults?")) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/teaching-excellence/settings/reset`, {}, { withCredentials: true });
      setSettings(data.settings);
      toast.success("Reset to defaults");
    } catch (e) {
      toast.error("Reset failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  const previewTier = useMemo(() => settings ? tierForScore(previewScore, settings) : null, [previewScore, settings]);

  if (loading || !settings) {
    return (
      <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <Link
            to="/admin"
            data-testid="back-to-admin-link"
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 min-h-[40px] px-2"
          >
            <ArrowLeft className="w-4 h-4" /> Admin
          </Link>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight">Teaching Excellence</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b border-slate-200" role="tablist">
          {["settings", "history"].map((t) => (
            <button
              key={t}
              data-testid={`tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px capitalize min-h-[44px] ${
                tab === t ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t === "settings" ? <SlidersHorizontal className="w-4 h-4 inline mr-1.5" /> : <History className="w-4 h-4 inline mr-1.5" />}
              {t}
            </button>
          ))}
        </div>

        {tab === "settings" && (
          <>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 mb-4">
              <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
                <Target className="w-4 h-4 text-blue-600" /> Eligibility
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FieldRow
                  k="enabled"
                  label="Enabled (1 = on, 0 = off)"
                  hint="When off, no Teaching Excellence chip is shown anywhere."
                  value={settings.enabled}
                  defaultVal={defaults.enabled}
                  onChange={(v) => setField("enabled", v)}
                  min={0} max={1}
                />
                <FieldRow
                  k="min_reviews"
                  label="Minimum completed reviews"
                  hint="Instructor must have at least N completed AI reviews to qualify."
                  value={settings.min_reviews}
                  defaultVal={defaults.min_reviews}
                  onChange={(v) => setField("min_reviews", v)}
                  min={1}
                />
                <FieldRow
                  k="recency_window_days"
                  label="Recency window (days)"
                  hint="Only reviews completed within this window count."
                  value={settings.recency_window_days}
                  defaultVal={defaults.recency_window_days}
                  onChange={(v) => setField("recency_window_days", v)}
                  min={7}
                />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 mb-4">
              <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
                <Award className="w-4 h-4 text-amber-500" /> Tier thresholds (mean review score, 0–100)
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {TIERS.map((t) => (
                  <FieldRow
                    key={t.key}
                    k={`${t.key}_score`}
                    label={`${t.label} threshold`}
                    hint={`Earned at mean score ≥ this value.`}
                    value={settings[`${t.key}_score`]}
                    defaultVal={defaults[`${t.key}_score`]}
                    onChange={(v) => setField(`${t.key}_score`, v)}
                    min={0} max={100}
                  />
                ))}
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Thresholds must be monotonic non-decreasing: bronze ≤ silver ≤ gold ≤ platinum ≤ black.
              </p>
            </div>

            {/* Preview */}
            <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-4 sm:p-5 mb-4" data-testid="preview-block">
              <h2 className="text-sm font-bold text-slate-800 mb-2">Preview</h2>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <span className="text-slate-700">Sample mean score:</span>
                  <input
                    type="number" min={0} max={100} value={previewScore}
                    onChange={(e) => setPreviewScore(Math.max(0, Math.min(100, Number(e.target.value || 0))))}
                    data-testid="preview-score-input"
                    className="w-20 rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </label>
                {previewTier ? (
                  <span data-testid="preview-tier-chip" className={`inline-flex items-center gap-1 rounded-full ring-1 font-bold text-xs px-2.5 py-1 ${TIERS.find((x) => x.key === previewTier).bg} ${TIERS.find((x) => x.key === previewTier).ring}`}>
                    <Sparkles className="w-3 h-3" />
                    Teaching Excellence · {TIERS.find((x) => x.key === previewTier).label}
                  </span>
                ) : (
                  <span className="text-xs text-slate-500">No tier earned at this score.</span>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
              <label className="block text-xs font-semibold text-slate-600 mb-1">Audit note (optional)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 500))}
                data-testid="audit-note-input"
                placeholder="Reason for this change (e.g. 'Tightened gold threshold ahead of launch')"
                className="w-full min-h-[80px] rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="mt-3 flex flex-wrap gap-2 justify-end">
                <button
                  onClick={reset}
                  disabled={saving}
                  data-testid="reset-button"
                  className="inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 rounded-md text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-60"
                >
                  <RotateCcw className="w-4 h-4" /> Reset to defaults
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="save-button"
                  className="inline-flex items-center justify-center gap-1.5 min-h-[44px] px-5 rounded-md text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save changes
                </button>
              </div>
            </div>
          </>
        )}

        {tab === "history" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6">
            <h2 className="text-sm font-bold text-slate-800 mb-3">Recent changes</h2>
            {historyLoading ? (
              <div className="flex items-center justify-center py-8 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : history.length === 0 ? (
              <p className="text-sm text-slate-500">No changes recorded yet.</p>
            ) : (
              <ol className="space-y-3">
                {history.map((h) => (
                  <li key={h.id} data-testid={`history-row-${h.id}`} className="rounded-xl border border-slate-200 p-3">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{h.action}</span>
                      <span className="text-xs text-slate-500">{fmtTime(h.created_at)}</span>
                    </div>
                    <div className="text-sm text-slate-700">
                      {h.admin_name || h.admin_email || "Admin"}
                      {h.note ? <span className="text-slate-500"> — “{h.note}”</span> : null}
                    </div>
                    <ul className="mt-2 text-xs text-slate-600 space-y-0.5">
                      {Object.entries(h.changes || {}).map(([k, v]) => (
                        <li key={k}><span className="font-mono text-slate-500">{k}:</span> <span className="line-through text-slate-400">{String(v.old)}</span> → <span className="font-semibold text-slate-800">{String(v.new)}</span></li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FieldRow({ k, label, hint, value, defaultVal, onChange, min, max }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="text-xs font-semibold text-slate-700">{label}</span>
        {defaultVal !== undefined && defaultVal !== value && (
          <span className="text-[10px] text-slate-400">default {defaultVal}</span>
        )}
      </div>
      <input
        type="number"
        data-testid={`field-${k}`}
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      {hint && <p className="mt-1 text-[11px] text-slate-500">{hint}</p>}
    </label>
  );
}
